import Phaser from "phaser";
import type { EnemyDef } from "../types";
import type { GridPath } from "./GridPath";
import { makeCreep, type CreepSprite } from "./CreepSprite";
import type { Tower } from "./Tower";

/** Per-enemy melee profile (damage to towers, range, attacks/sec).
 *  Cells are 48px; range 52-58 lets creeps swing at adjacent towers
 *  while 2-cell-away towers (≥96px) stay completely safe.
 */
const MELEE: Record<string, { damage: number; range: number; rate: number }> = {
  grunt: { damage: 1.5, range: 52, rate: 0.8 },
  fast:  { damage: 0.8, range: 52, rate: 1.2 },
  tank:  { damage: 3.0, range: 56, rate: 0.6 },
  boss:  { damage: 6.0, range: 60, rate: 0.7 },
};

export interface ActiveDot {
  dps: number;
  /** Game-time ms when this DOT expires. */
  expiresAt: number;
}

/**
 * Enemy that walks along the GridPath. Uses Phaser PathFollower for movement.
 */
export class Enemy {
  readonly def: EnemyDef;
  readonly maxHp: number;
  readonly speed: number;
  hp: number;
  /** 0..1 along the path. */
  t = 0;
  alive = true;
  reachedEnd = false;
  /** True once kill/leak callback has fired for this enemy. */
  resolved = false;

  readonly sprite: CreepSprite;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBar: Phaser.GameObjects.Rectangle;
  private dot?: ActiveDot;
  private dotTickAccum = 0;
  private meleeCooldown = 0;
  /** Active chill: speed scaled by `slowFactor` until `slowUntil` (game-time ms). */
  private slowFactor = 1;
  private slowUntil = 0;
  /** Frost overlay shown while chilled. */
  private chillTint?: Phaser.GameObjects.Arc;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly path: GridPath,
    def: EnemyDef,
    hpOverride?: number,
    speedOverride?: number,
  ) {
    this.def = def;
    this.maxHp = hpOverride ?? def.hp;
    this.speed = speedOverride ?? def.speed;
    this.hp = this.maxHp;

    const start = path.path.getStartPoint();
    this.sprite = makeCreep(scene, def.id, start.x, start.y);

    this.hpBarBg = scene.add.rectangle(start.x, start.y - def.radius - 8, def.radius * 2, 3, 0x000000, 0.6);
    this.hpBar = scene.add.rectangle(start.x, start.y - def.radius - 8, def.radius * 2, 3, 0x4ce053);
    this.hpBar.setOrigin(0.5, 0.5);
  }

  applyDot(dps: number, durationMs: number, now: number): void {
    const expiresAt = now + durationMs;
    if (!this.dot || dps > this.dot.dps || expiresAt > this.dot.expiresAt) {
      this.dot = { dps: Math.max(this.dot?.dps ?? 0, dps), expiresAt };
    }
  }

  /** Apply a chill: keeps the strongest (lowest factor) active and extends duration. */
  applySlow(factor: number, durationMs: number, now: number): void {
    if (!this.alive) return;
    const until = now + durationMs;
    // Strongest slow wins; weaker slows can still extend an expiring one.
    if (now >= this.slowUntil || factor <= this.slowFactor) {
      this.slowFactor = Math.min(factor, this.slowFactor === 1 ? factor : this.slowFactor);
    }
    this.slowUntil = Math.max(this.slowUntil, until);
    if (!this.chillTint) {
      this.chillTint = this.scene.add.circle(this.sprite.x, this.sprite.y, this.def.radius * 0.95, 0x7fd4ff, 0.28);
      this.chillTint.setDepth((this.sprite.depth ?? 0) + 1);
      this.scene.tweens.add({ targets: this.chillTint, alpha: { from: 0.28, to: 0.14 }, scale: { from: 1, to: 1.1 }, yoyo: true, repeat: -1, duration: 420, ease: "Sine.easeInOut" });
    }
  }

  takeDamage(dmg: number): boolean {
    if (!this.alive) return false;
    this.hp -= dmg;
    this.sprite.setTookHit();
    this.updateHpBar();
    if (this.hp <= 0) {
      this.kill();
      return true;
    }
    return false;
  }

  private kill(): void {
    this.alive = false;
    this.sprite.destroy();
    this.hpBar.destroy();
    this.hpBarBg.destroy();
    this.chillTint?.destroy();
    this.chillTint = undefined;
  }

  private leak(): void {
    this.alive = false;
    this.reachedEnd = true;
    this.sprite.destroy();
    this.hpBar.destroy();
    this.hpBarBg.destroy();
    this.chillTint?.destroy();
    this.chillTint = undefined;
  }

  /** Attack the nearest tower in melee range, if any. Spawns a swing flash + tower damage. */
  private tryAttackTowers(deltaMs: number, towers: Tower[]): void {
    this.meleeCooldown -= deltaMs;
    if (this.meleeCooldown > 0) return;
    const profile = MELEE[this.def.id];
    if (!profile) return;

    let nearest: Tower | undefined;
    let nearestDist = Infinity;
    for (const t of towers) {
      if (t.destroyed) continue;
      const dx = t.cell.x - this.sprite.x;
      const dy = t.cell.y - this.sprite.y;
      const d = Math.hypot(dx, dy);
      if (d <= profile.range && d < nearestDist) {
        nearest = t;
        nearestDist = d;
      }
    }
    if (!nearest) return;

    this.meleeCooldown = 1000 / profile.rate;

    // Visual swing line from creep to tower
    const line = this.scene.add.line(0, 0, this.sprite.x, this.sprite.y, nearest.cell.x, nearest.cell.y, 0xff5a3a, 0.9);
    line.setLineWidth(2);
    line.setOrigin(0, 0);
    this.scene.tweens.add({
      targets: line,
      alpha: 0,
      duration: 150,
      onComplete: () => line.destroy(),
    });

    nearest.takeDamage(profile.damage);
  }

  /** Advance along path. Returns true if still alive. */
  update(deltaMs: number, now: number, towers: Tower[] = []): boolean {
    if (!this.alive) return false;

    // Apply DOT
    if (this.dot && now < this.dot.expiresAt) {
      this.dotTickAccum += deltaMs;
      if (this.dotTickAccum >= 200) {
        const ticks = Math.floor(this.dotTickAccum / 200);
        const dmg = this.dot.dps * 0.2 * ticks;
        this.dotTickAccum -= ticks * 200;
        if (this.takeDamage(dmg)) return false;
      }
    } else if (this.dot && now >= this.dot.expiresAt) {
      this.dot = undefined;
    }

    // Chill expiry — restore full speed and drop the frost overlay.
    if (now >= this.slowUntil && this.slowFactor !== 1) {
      this.slowFactor = 1;
      this.chillTint?.destroy();
      this.chillTint = undefined;
    }

    const distance = this.speed * this.slowFactor * (deltaMs / 1000);
    const totalLen = this.path.pathLength;
    this.t += distance / totalLen;

    if (this.t >= 1) {
      this.leak();
      return false;
    }

    const point = this.path.path.getPoint(this.t);
    if (point) {
      this.sprite.setPosition(point.x, point.y);
      this.hpBar.setPosition(point.x, point.y - this.def.radius - 8);
      this.hpBarBg.setPosition(point.x, point.y - this.def.radius - 8);
      this.chillTint?.setPosition(point.x, point.y);
    }

    // Creeps no longer melee towers — heroes are permanent. The path itself
    // is the only way enemies can hurt the player (by leaking to the throne).
    void towers; void deltaMs;

    return true;
  }

  private updateHpBar(): void {
    const ratio = Math.max(0, this.hp / this.maxHp);
    this.hpBar.scaleX = ratio;
    if (ratio > 0.5) this.hpBar.fillColor = 0x4ce053;
    else if (ratio > 0.25) this.hpBar.fillColor = 0xffc23a;
    else this.hpBar.fillColor = 0xff5a3a;
  }

  get position(): { x: number; y: number } {
    return { x: this.sprite.x, y: this.sprite.y };
  }
}
